/**
 * Golden dragons in pixels (C0-VFX, ฝ่ามือมังกรทองเก้าสวรรค์ — an original art, dragonPalm/data.ts): a serpent body of
 * cached scale discs following a path, a head sprite (horns, mane, open jaw, whiskers) facing the way it flies, an
 * ink-and-gold brush trail behind it, gold drops shed as it goes and a burst of drops when it dissolves.
 *
 * The forms (`DRAGON_FORMS`) are timelines like the ink templates (render/ink/kinds.ts): t = 0 is the palm thrust out
 * (the motion's contact), negative t the wind-up (drops of the kind's colour gathering), `hit` when the blow lands.
 */
import { canvas, ctx2d } from '../pixel';
import { drawDrop, RES, type Pt, type Stroke } from './brush';
import { clamp01, easeIn, easeOut, hash2, span } from './noise';
import type { InkTone } from './palette';
import {
  arc,
  drawGather,
  drawSplats,
  memo,
  P,
  splats,
  stroke,
  type BloodHit,
  type InkLayer,
  type InkScene,
} from './samples';

/** The dragon's colours: gold (every form), or amber for the break (its kind's colour). */
interface Hide {
  out: string;
  body: string;
  lit: string;
  belly: string;
  fin: string;
}
const HIDES: Readonly<Record<'gold' | 'amber', Hide>> = {
  gold: { out: '#4a2a06', body: '#f6cf4a', lit: '#fff3b0', belly: '#c08a1c', fin: '#dc4430' },
  amber: { out: '#3a1a04', body: '#f59a2c', lit: '#ffd98a', belly: '#a95a14', fin: '#7a1520' },
};
type HideId = keyof typeof HIDES;

const DISCS = new Map<string, HTMLCanvasElement>();
/** A body segment of radius `r` art px: dark outline, gold body, lit upper right, darker belly lower left, scale nicks. */
function disc(hide: HideId, r: number): HTMLCanvasElement {
  const k = `${hide}${r}`;
  let c = DISCS.get(k);
  if (c) return c;
  const H = HIDES[hide];
  const R = r * RES;
  const n = Math.ceil(R) * 2 + 2;
  c = canvas(n, n);
  const g = ctx2d(c);
  const m = n / 2;
  for (let y = 0; y < n; y++)
    for (let x = 0; x < n; x++) {
      const dx = x + 0.5 - m;
      const dy = y + 0.5 - m;
      const d = Math.hypot(dx, dy);
      if (d > R) continue;
      let col = H.body;
      if (d > R - 1.2) col = H.out;
      else if (dx > R * 0.1 && dy < -R * 0.1 && d < R - 1.5) col = H.lit;
      else if (dx < -R * 0.2 && dy > R * 0.15) col = H.belly;
      else if ((x + y * 2) % 5 === 0 && d < R - 2) col = H.belly;
      g.fillStyle = col;
      g.fillRect(x, y, 1, 1);
    }
  DISCS.set(k, c);
  return c;
}

const HEADS = new Map<string, HTMLCanvasElement>();
/**
 * The head, facing right, for a body radius `r`: skull, snout, an open jaw with a red mouth, two horns swept back,
 * a red mane, an eye, whiskers; snapped to hard pixels with a dark outline.
 */
function head(hide: HideId, r: number): HTMLCanvasElement {
  const k = `${hide}${r}`;
  let c = HEADS.get(k);
  if (c) return c;
  const H = HIDES[hide];
  const u = r * RES;
  const w = Math.ceil(u * 6);
  const h = Math.ceil(u * 5);
  const ox = Math.round(u * 2.4);
  const oy = Math.round(u * 2.6);
  c = canvas(w, h);
  const g = ctx2d(c);
  const ell = (x: number, y: number, rx: number, ry: number, col: string) => {
    g.fillStyle = col;
    g.beginPath();
    g.ellipse(ox + x * u, oy + y * u, rx * u, ry * u, 0, 0, Math.PI * 2);
    g.fill();
  };
  const tri = (pts: [number, number][], col: string) => {
    g.fillStyle = col;
    g.beginPath();
    pts.forEach(([x, y], i) => (i ? g.lineTo(ox + x * u, oy + y * u) : g.moveTo(ox + x * u, oy + y * u)));
    g.closePath();
    g.fill();
  };
  // Mane behind the head.
  tri([[-1.4, -0.6], [-2.3, -1.2], [-1.6, 0.1], [-2.4, 0.4], [-1.3, 0.7]], H.fin);
  // Horns swept back.
  tri([[-0.3, -0.8], [-2.2, -2.2], [-0.9, -0.5]], H.lit);
  tri([[0.2, -0.9], [-1.4, -2.4], [-0.3, -0.6]], H.belly);
  // Lower jaw (open), mouth, skull, snout.
  tri([[0.2, 0.5], [2.6, 1.1], [2.4, 1.5], [0.3, 1.1]], H.belly);
  tri([[0.6, 0.45], [2.5, 0.95], [0.6, 0.95]], '#7a1520');
  ell(0, 0, 1.25, 1.05, H.body);
  ell(1.5, 0.15, 1.15, 0.55, H.body);
  ell(0.4, -0.4, 0.7, 0.4, H.lit);
  ell(1.9, -0.05, 0.5, 0.2, H.lit);
  // Eye and nostril.
  ell(0.35, -0.25, 0.28, 0.24, '#ffffff');
  ell(0.42, -0.24, 0.14, 0.16, H.fin);
  ell(2.45, -0.05, 0.12, 0.1, H.out);
  // Whiskers trailing back from the snout.
  g.strokeStyle = H.lit;
  g.lineWidth = Math.max(1, u * 0.18);
  g.beginPath();
  g.moveTo(ox + 2.2 * u, oy + 0.4 * u);
  g.quadraticCurveTo(ox + 0.8 * u, oy + 1.9 * u, ox - 1.6 * u, oy + 1.6 * u);
  g.stroke();
  // Hard pixels and a dark outline.
  const img = g.getImageData(0, 0, w, h);
  const d = new Uint32Array(img.data.buffer);
  const solid = new Uint8Array(w * h);
  for (let i = 0; i < d.length; i++) {
    if (d[i]! >>> 24 >= 110) {
      d[i] = d[i]! | 0xff000000;
      solid[i] = 1;
    } else d[i] = 0;
  }
  const out = (() => {
    const v = parseInt(H.out.slice(1), 16);
    return (0xff << 24) | ((v & 0xff) << 16) | (v & 0xff00) | ((v >> 16) & 0xff);
  })();
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (solid[i]) continue;
      if ((x > 0 && solid[i - 1]) || (x < w - 1 && solid[i + 1]) || (y > 0 && solid[i - w]) || (y < h - 1 && solid[i + w]))
        d[i] = out;
    }
  g.putImageData(img, 0, 0);
  HEADS.set(k, c);
  (c as HTMLCanvasElement & { o?: [number, number] }).o = [ox, oy];
  return c;
}

/** A path resampled by length: point and direction at `s` (0–1). */
interface Path {
  at(s: number): { x: number; y: number; dx: number; dy: number };
  pts: readonly Pt[];
}
function pathOf(pts: readonly Pt[]): Path {
  const len: number[] = [0];
  for (let i = 1; i < pts.length; i++) len.push(len[i - 1]! + Math.hypot(pts[i]![0] - pts[i - 1]![0], pts[i]![1] - pts[i - 1]![1]));
  const total = len[len.length - 1]! || 1;
  return {
    pts,
    at(s) {
      const d = clamp01(s) * total;
      let i = 1;
      while (i < len.length - 1 && len[i]! < d) i++;
      const a = pts[i - 1]!;
      const b = pts[i]!;
      const seg = len[i]! - len[i - 1]! || 1;
      const t = clamp01((d - len[i - 1]!) / seg);
      return { x: a[0] + (b[0] - a[0]) * t, y: a[1] + (b[1] - a[1]) * t, dx: (b[0] - a[0]) / seg, dy: (b[1] - a[1]) / seg };
    },
  };
}

/** Points on a wave from a to b (local art px), `amp` sideways, `turns` waves. */
function wave(a: Pt, b: Pt, amp: number, turns: number, n = 24): Pt[] {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const l = Math.hypot(dx, dy) || 1;
  return Array.from({ length: n }, (_, i) => {
    const u = i / (n - 1);
    const s = Math.sin(u * Math.PI * 2 * turns) * amp;
    return [a[0] + dx * u - (dy / l) * s, a[1] + dy * u + (dx / l) * s] as Pt;
  });
}
/** A quadratic curve a → c (through the pull of b). */
function curve(a: Pt, b: Pt, c: Pt, n = 24): Pt[] {
  return Array.from({ length: n }, (_, i) => {
    const t = i / (n - 1);
    const m = 1 - t;
    return [m * m * a[0] + 2 * m * t * b[0] + t * t * c[0], m * m * a[1] + 2 * m * t * b[1] + t * t * c[1]] as Pt;
  });
}

interface DragonOpts {
  /** Head radius (body tapers to a third), body length as a part of the path, the hide. */
  r: number;
  len: number;
  hide?: HideId;
  /** Only the segments in front of (true) or behind (false) the caster: `depth(s)` > 0 is in front. */
  front?: boolean;
  depth?: (s: number) => number;
  /** 0–1: the dragon dissolving (segments drop from the tail, drops burst from the head). */
  fade?: number;
}

/** A dragon on `path` with its head at `h` (0–1), in local coords of the scene. */
function drawDragon(g: CanvasRenderingContext2D, sc: InkScene, path: Path, h: number, o: DragonOpts): void {
  if (h <= 0) return;
  const hide = o.hide ?? 'gold';
  const n = Math.max(6, Math.round(o.r * 4.5));
  const keep = Math.round(n * (1 - clamp01(o.fade ?? 0)));
  // Tail to head.
  for (let i = n - 1; i >= 0; i--) {
    if (i >= keep) continue;
    const s = h - (i / (n - 1)) * o.len;
    if (s < 0) continue;
    if (o.depth && (o.depth(s) > 0) !== !!o.front) continue;
    const p = path.at(s);
    const r = Math.max(1, Math.round(o.r * (1 - (i / n) * 0.68) * 2) / 2);
    const c = disc(hide, r);
    const [x, y] = P(sc, p.x, p.y + Math.sin(i * 0.9 + h * 12) * 0.6);
    const w = c.width / RES;
    g.drawImage(c, Math.round((x - w / 2) * RES) / RES, Math.round((y - w / 2) * RES) / RES, w, w);
    // A red fin along the spine every third segment.
    if (i % 3 === 1 && r >= 1.5) {
      g.fillStyle = HIDES[hide].fin;
      g.fillRect(Math.round(x), Math.round(y - r - 1), 1, 1);
    }
  }
  if (keep <= 0) return;
  const p = path.at(h);
  if (o.depth && (o.depth(h) > 0) !== !!o.front) return;
  const hd = head(hide, o.r) as HTMLCanvasElement & { o?: [number, number] };
  const [ox, oy] = hd.o ?? [0, 0];
  const [x, y] = P(sc, p.x, p.y);
  const right = (sc.flip ? -p.dx : p.dx) >= 0;
  const W = hd.width / RES;
  const H = hd.height / RES;
  g.save();
  g.translate(Math.round(x * RES) / RES, Math.round(y * RES) / RES);
  if (!right) g.scale(-1, 1);
  g.drawImage(hd, -ox / RES, -oy / RES, W, H);
  g.restore();
}

/** Gold drops shed along the path behind the head, a few each frame. */
function shed(g: CanvasRenderingContext2D, sc: InkScene, path: Path, h: number, t: number, seed: number, hide: HideId = 'gold'): void {
  for (let i = 0; i < 5; i++) {
    const s = h - 0.05 - i * 0.07;
    if (s < 0) continue;
    const p = path.at(s);
    const age = (i + 1) * 0.06;
    const [x, y] = P(sc, p.x + (hash2(i, Math.floor(t * 20), seed) - 0.5) * 6, p.y + age * 40);
    drawDrop(g, hide === 'amber' ? 'break' : 'power', x, y, i < 2 ? 1.5 : 1, 'acc');
  }
}

/** The ink-and-gold brush trail along a path, painted behind the head, its tail wiped. */
function trail(g: CanvasRenderingContext2D, sc: InkScene, st: Stroke, h: number, len: number, fade: number, tone: InkTone = 'power'): void {
  stroke(g, st, tone, sc, clamp01(h), fade, Math.max(0, h - len));
}

const BURST = splats(950, 16, 0, 2.6, 150, 2.5, 0.7);
const BIG_BURST = splats(951, 28, -Math.PI / 2, 3.2, 200, 3.5, 0.6);

/** One form of the art. */
export interface DragonForm {
  id: string;
  /** When the blow lands (s after the palm), how long it runs, and how a landed blow bleeds. */
  hit: number;
  life: number;
  blood: BloodHit;
  draw(g: CanvasRenderingContext2D, t: number, sc: InkScene, layer: InkLayer, arrivals?: readonly number[]): void;
}

/** The wind-up: drops of the kind's colour drawn into the palm over the last `dur` s before it. */
function windup(g: CanvasRenderingContext2D, t: number, sc: InkScene, tone: InkTone, lx = 10, ly = -42, dur = 0.28): void {
  if (t < -dur || t >= 0) return;
  drawGather(g, tone, sc, lx, ly, 1 + t / dur, 9, 26, 960);
}

/** A dragon bursting from the palm at local y `ly`, from time `t0`, through the foe and on past it. */
const burstPath = memo((D, tdy) => ({
  hi: pathOf(wave([12, -46], [D + 36, tdy - 50], 5, 1.2)),
  lo: pathOf(wave([12, -38], [D + 34, tdy - 36], 4, 1.4)),
  hiSt: { pts: wave([12, -46], [D + 36, tdy - 50], 5, 1.2, 14), w: 5, shape: 'swell', dry: 0.6, seed: 970 } as Stroke,
  loSt: { pts: wave([12, -38], [D + 34, tdy - 36], 4, 1.4, 14), w: 4, shape: 'swell', dry: 0.6, seed: 971 } as Stroke,
  ring: { pts: arc(D - 4, tdy - 46, 12, 12, -2, -2 + Math.PI * 1.8, 14), w: 3.5, shape: 'press', dry: 0.5, body: 'acc', seed: 972 } as Stroke,
}));

function palmDragon(g: CanvasRenderingContext2D, t: number, sc: InkScene, which: 'hi' | 'lo', hitT: number): void {
  const S = burstPath(sc);
  const path = which === 'hi' ? S.hi : S.lo;
  const h = easeOut(span(t, 0, 0.26));
  const fade = span(t, 0.24, 0.4);
  trail(g, sc, which === 'hi' ? S.hiSt : S.loSt, h, 0.5, span(t, 0.22, 0.45));
  drawDragon(g, sc, path, h, { r: 4.5, len: 0.42, fade });
  if (t < 0.3) shed(g, sc, path, h, t, 973);
  stroke(g, S.ring, 'power', sc, easeOut(span(t, hitT, hitT + 0.08)), span(t, hitT + 0.15, hitT + 0.45));
  drawSplats(g, BURST, 'power', sc, sc.D - 4, sc.tdy - 46, t - hitT, sc.tdy + 2, 0.35);
}

const lancePath = memo((D, tdy) => {
  const pts = curve([12, -48], [D * 0.45, tdy - 96], [D - 2, tdy - 46]);
  return { path: pathOf(pts), st: { pts: pts.filter((_, i) => i % 2 === 0), w: 5, shape: 'swell', dry: 0.55, seed: 980 } as Stroke };
});

const twinPaths = memo((D, tdy) =>
  [-40, 0, 34].map((bend, i) => {
    const pts = curve([12, -48 + i * 4], [D * 0.5, tdy - 70 + bend], [D - 2, tdy - 48 + i * 6]);
    return { path: pathOf(pts), st: { pts: pts.filter((_, k) => k % 2 === 0), w: 3, shape: 'swell', dry: 0.6, seed: 990 + i } as Stroke };
  }),
);

const rendPath = memo((D, tdy) => {
  const pts = curve([-8, -112], [D * 0.3, tdy - 120], [D - 10, tdy - 40]);
  return { path: pathOf(pts), st: { pts: pts.filter((_, i) => i % 2 === 0), w: 6, shape: 'press', dry: 0.6, body: 'acc', seed: 1000 } as Stroke };
});

/** A helix round the caster: angle `a0` at the ground, rising `rise` px over `turns` turns, radius shrinking. */
function helix(a0: number, turns: number, r0: number, r1: number, rise: number, n = 40): { pts: Pt[]; ang: number[] } {
  const pts: Pt[] = [];
  const ang: number[] = [];
  for (let i = 0; i < n; i++) {
    const u = i / (n - 1);
    const a = a0 + u * turns * Math.PI * 2;
    const r = r0 + (r1 - r0) * u;
    pts.push([Math.cos(a) * r, Math.sin(a) * r * 0.45 - u * rise - 4]);
    ang.push(a);
  }
  return { pts, ang };
}
const stompPaths = memo(() =>
  [0, 1, 2, 3].map((k) => {
    const hx = helix(k * (Math.PI / 2) + 0.4, 1.1, 26, 12, 96);
    const path = pathOf(hx.pts);
    const depth = (s: number) => Math.sin(hx.ang[Math.min(hx.ang.length - 1, Math.round(s * (hx.ang.length - 1)))]!);
    return { path, depth };
  }),
);
const stompRing = memo(() => ({
  ring: { pts: arc(0, 0, 30, 13, -2.2, -2.2 + Math.PI * 1.85, 18), w: 5, shape: 'press', dry: 0.6, body: 'acc', seed: 1010 } as Stroke,
  cracks: Array.from({ length: 6 }, (_, i): Stroke => {
    const a = (i / 6) * Math.PI * 2 + 0.2;
    return {
      pts: [
        [Math.cos(a) * 6, Math.sin(a) * 2.6],
        [Math.cos(a) * 20, Math.sin(a) * 9],
      ],
      w: 2.5,
      shape: 'flick',
      dry: 0.5,
      seed: 1011 + i,
    };
  }),
}));
const DUST = splats(1020, 14, -Math.PI / 2, 3, 70, 2, 0.3);

const whirlPath = memo(() => {
  const pts: Pt[] = [];
  const ang: number[] = [];
  for (let i = 0; i < 48; i++) {
    const a = Math.PI * 0.8 + (i / 47) * Math.PI * 2.2;
    pts.push([Math.cos(a) * 32, Math.sin(a) * 13 - 30]);
    ang.push(a);
  }
  return { path: pathOf(pts), depth: (s: number) => Math.sin(ang[Math.min(47, Math.round(s * 47))]!) };
});

const skyPath = memo((D, tdy) => {
  const pts = [
    ...curve([D - 80, tdy - 230], [D + 50, tdy - 200], [D + 20, tdy - 140], 14),
    ...curve([D + 20, tdy - 140], [D - 40, tdy - 100], [D, tdy - 8], 16).slice(1),
  ];
  return {
    path: pathOf(pts),
    st: { pts: pts.filter((_, i) => i % 2 === 0), w: 9, shape: 'swell', dry: 0.55, seed: 1030 } as Stroke,
    ring: { pts: arc(D, tdy, 40, 17, -2.2, -2.2 + Math.PI * 1.85, 20), w: 6, shape: 'press', dry: 0.6, body: 'acc', seed: 1031 } as Stroke,
    ring2: { pts: arc(D, tdy, 24, 10, 0.9, 0.9 - Math.PI * 1.7, 16), w: 4, shape: 'press', dry: 0.5, seed: 1032 } as Stroke,
  };
});

export const DRAGON_FORMS: Readonly<Record<string, DragonForm>> = {
  // ฝ่ามือมังกรตื่น: a dragon bursts from the palm through the foe.
  'dp9-burst': {
    id: 'dp9-burst',
    hit: 0.12,
    life: 0.65,
    blood: 'spit',
    draw(g, t, sc, L) {
      if (L !== 'over') return;
      windup(g, t, sc, 'attack');
      palmDragon(g, t, sc, 'hi', 0.12);
    },
  },
  // มังกรทองพุ่งทะลวง: one dragon arcs out and strikes the far foe.
  'dp9-lance': {
    id: 'dp9-lance',
    hit: 0.32,
    life: 0.8,
    blood: 'spit',
    draw(g, t, sc, L) {
      if (L !== 'over') return;
      windup(g, t, sc, 'attack');
      const S = lancePath(sc);
      const h = easeIn(span(t, 0, 0.32)) * 0.6 + span(t, 0, 0.32) * 0.4;
      trail(g, sc, S.st, h, 0.45, span(t, 0.3, 0.6));
      drawDragon(g, sc, S.path, h, { r: 5, len: 0.36, fade: span(t, 0.32, 0.44) });
      if (t < 0.34) shed(g, sc, S.path, h, t, 981);
      drawSplats(g, BURST, 'power', sc, sc.D - 2, sc.tdy - 46, t - 0.32, sc.tdy + 2, 0.35);
    },
  },
  // มังกรคู่ฝ่าเมฆ: three small dragons on their own curves, landing one after another (the volley's times in the game).
  'dp9-twin': {
    id: 'dp9-twin',
    hit: 0.3,
    life: 0.9,
    blood: 'spit',
    draw(g, t, sc, L, arrivals) {
      if (L !== 'over') return;
      windup(g, t, sc, 'attack');
      const paths = twinPaths(sc);
      paths.forEach((p, i) => {
        const at = arrivals?.[i] ?? 0.3 + i * 0.09;
        const t0 = at - 0.3;
        const h = span(t, t0, at);
        trail(g, sc, p.st, h, 0.4, span(t, at - 0.02, at + 0.25));
        drawDragon(g, sc, p.path, h, { r: 3.2, len: 0.34, fade: span(t, at, at + 0.12) });
        drawSplats(g, BURST.slice(0, 7), 'power', sc, sc.D - 2, sc.tdy - 48 + i * 6, t - at, sc.tdy + 2, 0.3);
      });
    },
  },
  // ฝ่ามือมังกรผ่าศิลา (break): an amber dragon dives on the foe's guard from above; the cast shows the amber tell.
  'dp9-rend': {
    id: 'dp9-rend',
    hit: 0.12,
    life: 0.7,
    blood: 'spit',
    draw(g, t, sc, L) {
      if (L === 'ground') return;
      const S = rendPath(sc);
      const h = easeIn(span(t, 0, 0.12));
      trail(g, sc, S.st, h, 0.5, span(t, 0.12, 0.45), 'break');
      drawDragon(g, sc, S.path, h, { r: 5, len: 0.4, hide: 'amber', fade: span(t, 0.14, 0.3) });
      drawSplats(g, BURST, 'break', sc, sc.D - 10, sc.tdy - 42, t - 0.12, sc.tdy + 2, 0.4);
    },
  },
  // กระทืบปฐพีปลุกมังกร (buff): the foot stamps, the ground cracks in a gold ring, four dragons rise circling the caster.
  'dp9-stomp': {
    id: 'dp9-stomp',
    hit: 0,
    life: 1.4,
    blood: 'spit',
    draw(g, t, sc, L) {
      const R = stompRing(sc);
      const own = { ...sc, D: 0, tdy: 0 };
      if (L === 'ground') {
        windup(g, t, own, 'buff', 0, -4, 0.3);
        stroke(g, R.ring, 'power', own, easeOut(span(t, 0, 0.14)), span(t, 0.9, 1.35));
        for (const c of R.cracks) stroke(g, c, 'power', own, easeOut(span(t, 0, 0.08)), span(t, 0.6, 1.0));
      } else drawSplats(g, DUST, 'power', own, 0, -2, t, 2, 0.4);
      stompPaths(sc).forEach((p, k) => {
        const t0 = 0.05 + k * 0.07;
        const h = easeOut(span(t, t0, t0 + 0.95));
        drawDragon(g, own, p.path, h, { r: 3.6, len: 0.3, depth: p.depth, front: L === 'over', fade: span(t, t0 + 0.9, t0 + 1.15) });
      });
    },
  },
  // มังกรวนวงทอง: one dragon circles the caster once, in front and behind.
  'dp9-whirl': {
    id: 'dp9-whirl',
    hit: 0.25,
    life: 0.8,
    blood: 'spit',
    draw(g, t, sc, L) {
      const own = { ...sc, D: 0, tdy: 0 };
      windup(g, t, own, 'attack', 0, -40);
      const W = whirlPath(sc);
      const h = span(t, 0, 0.5);
      drawDragon(g, own, W.path, h, { r: 4.5, len: 0.35, depth: W.depth, front: L === 'over', fade: span(t, 0.48, 0.62) });
    },
  },
  // ฝ่ามือมังกรซ้อนคู่: two palms, a dragon from each, one after the other.
  'dp9-double': {
    id: 'dp9-double',
    hit: 0.12,
    life: 0.85,
    blood: 'spit',
    draw(g, t, sc, L) {
      if (L !== 'over') return;
      windup(g, t, sc, 'attack');
      palmDragon(g, t, sc, 'hi', 0.12);
      palmDragon(g, t - 0.18, sc, 'lo', 0.12);
    },
  },
  // มังกรทองเก้าสวรรค์ (power): a great dragon comes down out of the sky onto the foe; the ground bursts in gold.
  'dp9-sky': {
    id: 'dp9-sky',
    hit: 0.55,
    life: 1.4,
    blood: 'spit',
    draw(g, t, sc, L) {
      const S = skyPath(sc);
      if (L === 'ground') {
        windup(g, t, sc, 'power', 0, -90, 0.4);
        stroke(g, S.ring, 'power', sc, easeOut(span(t, 0.55, 0.7)), span(t, 1.0, 1.4));
        stroke(g, S.ring2, 'power', sc, easeOut(span(t, 0.58, 0.72)), span(t, 0.9, 1.3));
        return;
      }
      const h = easeIn(span(t, 0, 0.55)) * 0.7 + span(t, 0, 0.55) * 0.3;
      trail(g, sc, S.st, h, 0.4, span(t, 0.55, 1.0));
      drawDragon(g, sc, S.path, h, { r: 9, len: 0.42, fade: span(t, 0.6, 0.8) });
      if (t < 0.56) shed(g, sc, S.path, h, t, 1033);
      drawSplats(g, BIG_BURST, 'power', sc, sc.D, sc.tdy - 10, t - 0.55, sc.tdy + 2, 0.6);
    },
  },
};

/** The basic chain's finisher of the art: a small dragon from the palm (the ninth form). */
export const DRAGON_FINISHER: DragonForm = {
  id: 'dp9-basic',
  hit: 0.12,
  life: 0.6,
  blood: 'spit',
  draw(g, t, sc, L) {
    if (L === 'over') palmDragon(g, t, sc, 'lo', 0.12);
  },
};

export const isDragon = (skillId: string): boolean => skillId in DRAGON_FORMS;
